/**
 * SAT HTC™ · Mentor Cockpit (M3)
 * Assignment-Isolation: nur zugewiesene Klienten sichtbar.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("sat_token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function HtcMentorCockpit() {
  const [clients, setClients] = useState([]);
  const [targetsByOwner, setTargetsByOwner] = useState({});
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => { loadClients(); }, []);

  async function loadClients() {
    try {
      const r = await axios.get(`${API}/api/htc/mentor/clients`, { headers: authHeaders() });
      setClients(r.data.clients);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  async function loadTargets(owner) {
    try {
      const r = await axios.get(`${API}/api/htc/mentor/clients/${owner}/targets`, { headers: authHeaders() });
      setTargetsByOwner(prev => ({ ...prev, [owner]: r.data.targets }));
      setSelectedOwner(owner);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  async function reviewTarget(targetId, decision) {
    try {
      await axios.post(`${API}/api/htc/mentor/targets/${targetId}/review`, {
        owner_user_id: selectedOwner, decision,
      }, { headers: authHeaders() });
      await loadTargets(selectedOwner);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  async function activateTarget(targetId) {
    try {
      await axios.post(`${API}/api/htc/mentor/targets/${targetId}/activate`, {
        owner_user_id: selectedOwner,
      }, { headers: authHeaders() });
      await loadTargets(selectedOwner);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || String(e));
    }
  }

  return (
    <div className="min-h-screen bg-[#0b0f1a] text-slate-100" data-testid="htc-mentor-cockpit">
      <div className="mx-auto max-w-4xl px-6 py-14">
        <p className="mb-1 text-xs uppercase tracking-widest text-slate-400">SAT HTC™ · Mentor</p>
        <h1 className="mb-8 text-3xl font-light">Cockpit</h1>

        {error && <div className="mb-4 rounded border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-200"
                       data-testid="htc-mentor-error">{error}</div>}

        <section className="mb-8">
          <h2 className="mb-3 text-lg">Zugewiesene Klient:innen</h2>
          {clients.length === 0 && (
            <p className="text-sm text-slate-400" data-testid="htc-mentor-no-clients">
              Zurzeit keine Zuweisungen.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {clients.map(c => (
              <button key={c} onClick={() => loadTargets(c)}
                       data-testid={`htc-mentor-client-${c}`}
                       className={`rounded border px-3 py-1.5 text-sm ${
                         selectedOwner === c
                          ? "border-sky-400 text-sky-200"
                          : "border-slate-700 text-slate-200 hover:bg-slate-800"
                       }`}>
                {c}
              </button>
            ))}
          </div>
        </section>

        {selectedOwner && (
          <section>
            <h2 className="mb-3 text-lg">Transformationsziele · {selectedOwner}</h2>
            <div className="space-y-3">
              {(targetsByOwner[selectedOwner] || []).map(t => (
                <div key={t.target_id}
                     data-testid={`htc-mentor-target-${t.target_id}`}
                     className="rounded-xl border border-slate-800 bg-slate-900/40 p-4">
                  <div className="mb-2 text-xs uppercase tracking-widest text-slate-400">
                    {t.htc_code_ref} · {t.htc_movement_ref} · {t.state}
                  </div>
                  <p className="mb-3 text-sm text-slate-200">
                    {t.target_definition || "— noch nicht formuliert —"}
                  </p>
                  <p className="mb-3 text-xs text-slate-500">
                    Signals: {t.change_signal_count} · Integration: {t.integration_signal_count}
                  </p>
                  {t.state === "CLIENT_SELECTED" && (
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => reviewTarget(t.target_id, "SUPPORTED")}
                              data-testid="htc-mentor-review-supported"
                              className="rounded bg-emerald-500 px-3 py-1 text-xs text-white hover:bg-emerald-400">
                        Freigeben
                      </button>
                      <button onClick={() => reviewTarget(t.target_id, "CLARIFY")}
                              data-testid="htc-mentor-review-clarify"
                              className="rounded bg-amber-500/80 px-3 py-1 text-xs text-white">
                        Rückfrage
                      </button>
                      <button onClick={() => reviewTarget(t.target_id, "REJECTED")}
                              data-testid="htc-mentor-review-rejected"
                              className="rounded border border-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-800">
                        Ablehnen
                      </button>
                    </div>
                  )}
                  {t.state === "MENTOR_REVIEWED" && (
                    <button onClick={() => activateTarget(t.target_id)}
                            data-testid="htc-mentor-activate-btn"
                            className="rounded bg-sky-500 px-3 py-1 text-xs text-white hover:bg-sky-400">
                      Aktivieren
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
